import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search,
  ArrowRight,
  Home,
  Briefcase,
  Plug,
  Settings as SettingsIcon,
  CreditCard,
  Plus,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react';
import { useAuthRoles } from '../../../../hooks/useAuthRoles';
import { useAllJobOffers } from '../../../../hooks/useJobOffers';
import { useTalents } from '../../../../hooks/useTalentPool';

type CommandGroupId = 'navigation' | 'actions' | 'recent_jobs' | 'talents';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  group: CommandGroupId;
  onSelect: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const GROUP_ORDER: CommandGroupId[] = ['navigation', 'actions', 'talents', 'recent_jobs'];

const isMacPlatform = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
};

export const CommandPalette = ({ open, onClose }: CommandPaletteProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { isAdmin } = useAuthRoles();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { data: recentJobsData } = useAllJobOffers(
    true,
    1,
    5,
    undefined,
    '',
    { refetchInterval: false, enabled: open },
    isAdmin ? 'admin' : 'user',
  );

  // Debounced server-side talent search — only fires when the palette is open and
  // there is a real query, so we never fetch the whole pool on open.
  const [debouncedQuery, setDebouncedQuery] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(id);
  }, [query]);
  const talentSearch = debouncedQuery.trim();
  const { data: talentsData } = useTalents(
    { search: talentSearch },
    { enabled: open && talentSearch.length >= 2 },
  );

  const items: CommandItem[] = useMemo(() => {
    const navItems: CommandItem[] = [
      {
        id: 'nav-home',
        label: t('nav.home'),
        description: t('command_palette.nav.home_desc'),
        icon: <Home className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/home');
          onClose();
        },
      },
      {
        id: 'nav-jobs',
        label: t('nav.jobs'),
        description: t('command_palette.nav.jobs_desc'),
        icon: <Briefcase className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/jobs');
          onClose();
        },
      },
      {
        id: 'nav-talent-pool',
        label: t('nav.talent_pool'),
        description: t('command_palette.nav.talent_pool_desc'),
        icon: <Users className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/talent-pool');
          onClose();
        },
      },
      {
        id: 'nav-agents',
        label: t('nav.agents'),
        description: t('command_palette.nav.agents_desc'),
        icon: <Sparkles className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/agents');
          onClose();
        },
      },
      {
        id: 'nav-integrations',
        label: t('nav.integrations'),
        description: t('command_palette.nav.integrations_desc'),
        icon: <Plug className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/integrations');
          onClose();
        },
      },
      {
        id: 'nav-settings',
        label: t('nav.settings'),
        description: t('command_palette.nav.settings_desc'),
        icon: <SettingsIcon className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/settings');
          onClose();
        },
      },
      {
        id: 'nav-billing',
        label: t('plan_chip.plans_and_billing'),
        description: t('command_palette.nav.billing_desc'),
        icon: <CreditCard className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/billing');
          onClose();
        },
      },
    ];

    if (isAdmin) {
      navItems.push({
        id: 'nav-admin',
        label: t('nav.admin'),
        description: t('command_palette.nav.admin_desc'),
        icon: <Shield className="w-4 h-4" />,
        group: 'navigation',
        onSelect: () => {
          navigate('/admin');
          onClose();
        },
      });
    }

    const actionItems: CommandItem[] = [
      {
        id: 'action-new-job',
        label: t('command_palette.actions.create_job'),
        description: t('command_palette.actions.create_job_desc'),
        icon: <Plus className="w-4 h-4" />,
        group: 'actions',
        onSelect: () => {
          navigate('/jobs?new=1');
          onClose();
        },
      },
    ];

    const jobItems: CommandItem[] = (recentJobsData?.items ?? []).map((job) => ({
      id: `job-${job.id}`,
      label: job.title,
      description: job.companyName || undefined,
      icon: <Briefcase className="w-4 h-4" />,
      group: 'recent_jobs',
      onSelect: () => {
        navigate(`/job/${job.id}`);
        onClose();
      },
    }));

    return [...navItems, ...actionItems, ...jobItems];
  }, [recentJobsData, navigate, onClose, isAdmin, t]);

  // Talent results are already server-filtered by the query, so they bypass the
  // client-side label/description filter applied to the static items below.
  const talentItems: CommandItem[] = useMemo(
    () =>
      (talentsData ?? []).slice(0, 6).map((talent) => ({
        id: `talent-${talent.id}`,
        label: talent.fullName,
        description: talent.email || undefined,
        icon: <Users className="w-4 h-4" />,
        group: 'talents',
        onSelect: () => {
          navigate(`/talent-pool/${talent.id}`);
          onClose();
        },
      })),
    [talentsData, navigate, onClose],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const staticFiltered = !q
      ? items
      : items.filter(
          (item) =>
            item.label.toLowerCase().includes(q) ||
            item.description?.toLowerCase().includes(q),
        );
    return [...staticFiltered, ...talentItems];
  }, [items, query, talentItems]);

  const grouped = useMemo(() => {
    const map = new Map<CommandGroupId, CommandItem[]>();
    for (const item of filtered) {
      const arr = map.get(item.group) ?? [];
      arr.push(item);
      map.set(item.group, arr);
    }
    return GROUP_ORDER
      .filter((groupId) => (map.get(groupId)?.length ?? 0) > 0)
      .map((groupId) => ({
        groupId,
        label: t(`command_palette.groups.${groupId}`),
        items: map.get(groupId)!,
      }));
  }, [filtered, t]);

  // Flattened in the exact rendered (GROUP_ORDER) order, so keyboard selection
  // indexes match the on-screen rows.
  const flatItems = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebouncedQuery('');
      setSelectedIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-cmd-index="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, flatItems.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatItems[selectedIndex];
        item?.onSelect();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, flatItems, selectedIndex, onClose]);

  if (!open) return null;

  const isMac = isMacPlatform();
  let globalIndex = -1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh] px-4 bg-dark-invirtus/40 animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={t('command_palette.aria_label')}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-light-lila/40 bg-light shadow-[0_24px_60px_-12px_rgba(16,24,40,0.25)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 h-12 px-3.5 border-b border-light-lila/40">
          <Search className="w-4 h-4 text-dark-invirtus/50 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('command_palette.placeholder')}
            className="flex-1 bg-transparent text-sm text-dark-invirtus font-body placeholder:text-dark-invirtus/40 focus:outline-none"
          />
          <kbd className="inline-flex items-center justify-center h-5 px-1.5 rounded border border-light-lila/60 bg-background-gray text-[10px] font-semibold text-dark-invirtus/50 font-body shrink-0">
            esc
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[55vh] overflow-y-auto py-1">
          {flatItems.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-sm text-dark-invirtus font-body">{t('command_palette.no_results')}</p>
              <p className="text-xs text-dark-invirtus/55 font-body mt-1">
                {t('command_palette.no_results_hint')}
              </p>
            </div>
          ) : (
            grouped.map(({ groupId, label, items: groupItems }) => (
              <div key={groupId} className="px-1.5 py-1">
                <div className="px-2.5 py-1 text-[10px] uppercase tracking-[0.08em] font-medium text-dark-invirtus/45 font-body">
                  {label}
                </div>
                <ul>
                  {groupItems.map((item) => {
                    globalIndex += 1;
                    const myIndex = globalIndex;
                    const isSelected = myIndex === selectedIndex;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          data-cmd-index={myIndex}
                          onClick={() => item.onSelect()}
                          onMouseEnter={() => setSelectedIndex(myIndex)}
                          className={`w-full flex items-center gap-3 h-10 px-2.5 rounded-md text-left transition-colors ${
                            isSelected
                              ? 'bg-dark-invirtus-soft'
                              : 'hover:bg-dark-invirtus-soft/50'
                          }`}
                        >
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded text-dark-invirtus/65 shrink-0">
                            {item.icon}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-dark-invirtus font-body truncate">
                              {item.label}
                            </span>
                            {item.description && (
                              <span className="block text-[11px] text-dark-invirtus/55 font-body truncate">
                                {item.description}
                              </span>
                            )}
                          </span>
                          {isSelected && (
                            <ArrowRight className="w-3.5 h-3.5 text-dark-invirtus/40 shrink-0" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-light-lila/40 px-3 py-1.5 flex items-center gap-3 text-[10px] text-dark-invirtus/55 font-body bg-background-gray/40">
          <span className="inline-flex items-center gap-1">
            <kbd className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded border border-light-lila/60 bg-light text-[9px] font-semibold">
              ↑↓
            </kbd>
            {t('command_palette.footer.navigate')}
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded border border-light-lila/60 bg-light text-[9px] font-semibold">
              ↵
            </kbd>
            {t('command_palette.footer.select')}
          </span>
          <span className="inline-flex items-center gap-1 ml-auto">
            <kbd className="inline-flex items-center justify-center h-4 px-1 rounded border border-light-lila/60 bg-light text-[9px] font-semibold">
              {isMac ? '⌘' : 'Ctrl'}
            </kbd>
            <kbd className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded border border-light-lila/60 bg-light text-[9px] font-semibold">
              K
            </kbd>
            {t('command_palette.footer.toggle')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
